// Web-only dismissible banner promoting the Android app on Google Play.
// Hidden on native (Capacitor) shells and once the user dismisses it.
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { isNativeApp } from "@/lib/platform";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "fitaura.installBanner.dismissed";
const PLAY_URL = "https://play.google.com/store/apps/details?id=com.fitauraai.app";

export const InstallAppBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isNativeApp()) return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch { /* ignore */ }
    // Only show on Android-ish web user-agents to avoid nagging desktop users.
    const ua = navigator.userAgent || "";
    const isAndroidWeb = /Android/i.test(ua);
    if (!isAndroidWeb) return;
    setVisible(true);
  }, []);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className={cn(
        "fixed inset-x-3 bottom-24 z-50 mx-auto flex max-w-md items-center gap-3 rounded-2xl",
        "border border-primary/30 bg-card/95 p-3 shadow-elevated backdrop-blur-xl",
        "lg:bottom-6 lg:left-auto lg:right-6 lg:mx-0",
        "animate-fade-in",
      )}
      role="region"
      aria-label="Install FitAura AI from Google Play"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-bold leading-tight">Faster on the app</p>
        <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
          Get FitAura AI on the Play Store — quicker try-ons, push updates, offline ready.
        </p>
      </div>
      <a
        href={PLAY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 transition-transform active:scale-95"
        aria-label="Get FitAura AI on Google Play"
      >
        <img src="/google-play-badge.svg" alt="Get it on Google Play" className="h-10 w-auto" />
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-white/5 hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
